// Cross-document cohesion: does the body actually deliver on the description?
// Extracts the description's content terms and measures how many appear in the
// body. Skills that promise X in the description but never mention X again in
// the body are a common failure mode our other rules don't catch.

const STOPWORDS = new Set([
  'a','an','the','and','or','but','if','of','to','for','with','from','on','in','at',
  'by','as','is','are','was','were','be','been','being','this','that','these','those',
  'it','its','use','uses','using','used','when','user','users','can','will','would',
  'should','may','might','help','helps','about','into','onto','via','than','then',
  'also','more','most','some','any','all','each','many','your','their','them','they',
  'have','has','had','do','does','did','this','that','what','which','who','how','why',
  'where','here','there','what','set','add','make','take','say','says','asks','want',
  'wants','need','needs','one','two','three','first','second','specific','specifically',
  'automatically','only','just','etc','include','includes','including','example',
  'examples','skill','skills',
]);

export interface CohesionMetrics {
  descriptionContentTerms: string[];
  bodyContentTerms: string[];
  sharedTerms: string[];
  coverage: number;             // shared / description terms
  bodyExpandsOnDescription: boolean;
  hollowPromiseTerms: string[]; // description terms missing from body
}

function tokenize(s: string): string[] {
  return s.toLowerCase().replace(/[^a-z0-9\s-]/g, ' ').split(/\s+/).filter(Boolean);
}

function contentTerms(text: string): string[] {
  const out = new Set<string>();
  for (const tok of tokenize(text)) {
    if (tok.length < 4) continue;
    if (STOPWORDS.has(tok)) continue;
    if (/^\d+$/.test(tok)) continue;
    out.add(tok);
  }
  return [...out];
}

// Light stemming: drop trailing s, ed, ing — so "creates" matches "creating".
function stem(tok: string): string {
  if (tok.length > 5 && tok.endsWith('ing')) return tok.slice(0, -3);
  if (tok.length > 4 && tok.endsWith('ed')) return tok.slice(0, -2);
  if (tok.length > 4 && tok.endsWith('es')) return tok.slice(0, -2);
  if (tok.length > 4 && tok.endsWith('s') && !tok.endsWith('ss')) return tok.slice(0, -1);
  return tok;
}

export function analyzeCohesion(description: string, body: string): CohesionMetrics {
  const descRaw = contentTerms(description);
  const bodyRaw = contentTerms(body);
  if (descRaw.length === 0) {
    return {
      descriptionContentTerms: [],
      bodyContentTerms: bodyRaw,
      sharedTerms: [],
      coverage: 0,
      bodyExpandsOnDescription: false,
      hollowPromiseTerms: [],
    };
  }
  const bodyStems = new Set(bodyRaw.map(stem));
  const shared: string[] = [];
  const missing: string[] = [];
  for (const t of descRaw) {
    if (bodyStems.has(stem(t))) shared.push(t);
    else missing.push(t);
  }
  const coverage = shared.length / descRaw.length;
  return {
    descriptionContentTerms: descRaw,
    bodyContentTerms: bodyRaw,
    sharedTerms: shared,
    coverage,
    bodyExpandsOnDescription: bodyRaw.length > descRaw.length * 2,
    hollowPromiseTerms: missing,
  };
}
